import React from 'react';
import { Input, DatePicker } from 'antd';
import dayjs from 'dayjs';
import { F919 } from '../../../services/pw9119Api';
import { Mode } from '../hooks/usePW9119Mode';

const { TextArea } = Input;

interface FieldErrorProps {
  error?: string;
}

const FieldError: React.FC<FieldErrorProps> = ({ error }) =>
  error ? (
    <span style={{ color: '#ff4d4f', fontSize: 11, marginLeft: 6, fontWeight: 'normal' }}>
      {error}
    </span>
  ) : null;

interface FormFieldsProps {
  formData: Partial<F919>;
  names: Record<string, string>;
  isEditing: boolean;
  mode: Mode;
  getField: <K extends keyof F919>(key: K) => F919[K] | string;
  setField: <K extends keyof F919>(key: K, value: F919[K]) => void;
  resolveSingleName: (nameKey: string, type: string, code: string) => void;
  getFieldError?: (field: string) => string | undefined;
}

const FormFields: React.FC<FormFieldsProps> = ({
  names, isEditing, mode,
  getField, setField, resolveSingleName, getFieldError,
}) => {
  const E = (field: string) => <FieldError error={getFieldError?.(field)} />;
  const isAddMode = mode === 'add';

  const parseDateValue = (val: string | null | undefined) => {
    if (!val) return null;
    const d = dayjs(val);
    return d.isValid() ? d : null;
  };

  return (
    <div className="pw9119-form-fields">
      {/* Row 1: Card No */}
      <div className="pw9119-field-row">
        <div className="pw9119-field-group pw9119-field-wide">
          <label className="required">刷卡卡號{E('f919_01')}</label>
          <Input
            style={{ width: 180 }}
            maxLength={10}
            disabled={!isAddMode}
            value={getField('f919_01') as string}
            onChange={e => setField('f919_01', e.target.value)}
            placeholder="刷卡卡號（最多10碼）"
          />
        </div>
      </div>

      {/* Row 2: Employee */}
      <div className="pw9119-field-row">
        <div className="pw9119-field-group">
          <label className="required">員工編號{E('f919_11')}</label>
          <div className="pw9119-lookup-field">
            <Input
              style={{ width: 120 }}
              maxLength={8}
              disabled={!isEditing}
              value={getField('f919_11') as string}
              onChange={e => setField('f919_11', e.target.value as F919['f919_11'])}
              onBlur={() => resolveSingleName('employee', 'employee', getField('f919_11') as string)}
              placeholder="員工編號"
            />
            <span className="pw9119-lookup-name">{names.employee || '--'}</span>
          </div>
        </div>
      </div>

      {/* Row 3: Department */}
      <div className="pw9119-field-row">
        <div className="pw9119-field-group">
          <label className="required">歸屬部門{E('f919_14')}</label>
          <div className="pw9119-lookup-field">
            <Input
              style={{ width: 120 }}
              maxLength={4}
              disabled={!isEditing}
              value={getField('f919_14') as string}
              onChange={e => setField('f919_14', e.target.value as F919['f919_14'])}
              onBlur={() => resolveSingleName('department', 'department', getField('f919_14') as string)}
              placeholder="部門代碼"
            />
            <span className="pw9119-lookup-name">{names.department || '--'}</span>
          </div>
        </div>
      </div>

      {/* Row 4: Issue date */}
      <div className="pw9119-field-row">
        <div className="pw9119-field-group">
          <label>領用日期{E('f919_12')}</label>
          <DatePicker
            style={{ width: 160 }}
            disabled={!isEditing}
            value={parseDateValue(getField('f919_12') as string | null)}
            onChange={(_date, dateStr) => {
              const v = Array.isArray(dateStr) ? dateStr[0] : dateStr;
              setField('f919_12', v || null);
            }}
            placeholder="選擇日期"
            format="YYYY-MM-DD"
          />
        </div>
      </div>

      {/* Row 5: Return date */}
      <div className="pw9119-field-row">
        <div className="pw9119-field-group">
          <label>還卡日期{E('f919_13')}</label>
          <DatePicker
            style={{ width: 160 }}
            disabled={!isEditing}
            value={parseDateValue(getField('f919_13') as string | null)}
            onChange={(_date, dateStr) => {
              const v = Array.isArray(dateStr) ? dateStr[0] : dateStr;
              setField('f919_13', v || null);
            }}
            placeholder="選擇日期"
            format="YYYY-MM-DD"
          />
        </div>
      </div>

      {/* Row 6: Remark */}
      <div className="pw9119-field-row">
        <div className="pw9119-field-group pw9119-field-full">
          <label>備註{E('f919_15')}</label>
          <TextArea
            style={{ width: 480 }}
            rows={3}
            disabled={!isEditing}
            value={getField('f919_15') as string}
            onChange={e => setField('f919_15', e.target.value as F919['f919_15'])}
            placeholder="備註說明"
          />
        </div>
      </div>

      {/* Row 7: Audit info (readonly) */}
      {getField('f919_91') && (
        <div className="pw9119-field-row pw9119-audit-row">
          <span className="pw9119-audit-label">建立者：{getField('f919_91') as string}</span>
          {getField('f919_92') && (
            <span className="pw9119-audit-label">建立日期：{getField('f919_92') as string}</span>
          )}
          {getField('f919_93') && (
            <span className="pw9119-audit-label">修改者：{getField('f919_93') as string}</span>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(FormFields);
